var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { Box, Popover, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { defaultCardHeight, defaultElementsPerPage, defaultFilters, defaultPageSize, notificationColors, scrollablePaperId, } from '../../constants';
import { NotificationsContext } from '../../contexts/NotificationsContext';
import { EndMessage } from '../end-message';
import { Loader } from '../loader';
import { NotificationCard } from '../notification-card';
import { NotificationPanelHeader } from '../notification-panel-header';
var card = notificationColors.card;
var makeSxStyles = function (_a) {
    var containerHeight = _a.containerHeight;
    return {
        paperContainer: {
            borderRadius: '4px',
            maxWidth: '500px',
            width: '100%',
            background: card.background,
        },
        messagesContainer: {
            maxHeight: containerHeight,
            overflow: 'auto',
            padding: function (theme) { return "0 ".concat(theme.spacing(1.5)); },
            background: function (theme) { return theme.palette.background.paper; },
        },
        listTransitionExit: {
            opacity: 1,
        },
        listTransitionExitActive: {
            opacity: 0,
            transition: function (theme) {
                return theme.transitions.create('opacity', {
                    easing: theme.transitions.easing.easeInOut,
                    duration: theme.transitions.duration.short,
                });
            },
        },
    };
};
var useStyles = makeStyles(function () {
    var theme = useTheme();
    return {
        listTransitionExit: {
            opacity: 1,
        },
        listTransitionExitActive: {
            opacity: 0,
            transition: theme.transitions.create('opacity', {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.short,
            }),
        },
    };
});
var loader = React.createElement(Loader, null);
export var NotificationPanel = function (props) {
    var _a = props.elementsPerPage, elementsPerPage = _a === void 0 ? defaultElementsPerPage : _a, loadNotifications = props.loadNotifications, anchorEl = props.anchorEl, setPopoverClose = props.setPopoverClose, isOpen = props.isOpen, changeViewed = props.changeViewed, setNotViewedNotifications = props.setNotViewedNotifications, notViewedNotifications = props.notViewedNotifications, removeNotification = props.deleteNotification, loadCountNotViewedMessages = props.loadCountNotViewedMessages;
    var _b = useState([]), notifications = _b[0], setNotifications = _b[1];
    var _c = useState(false), isNotificationsLoading = _c[0], setIsNotificationsLoading = _c[1];
    var _d = useState(false), needLoading = _d[0], setNeedLoading = _d[1];
    var _e = useState(true), hasMore = _e[0], setHasMore = _e[1];
    var containerHeight = useMemo(function () {
        return elementsPerPage * defaultCardHeight;
    }, [elementsPerPage]);
    var sx = makeSxStyles({ containerHeight: containerHeight });
    var classes = useStyles();
    var handleLoadedNotifications = function (notificationsList) {
        var listLength = notificationsList.length;
        if (!listLength || listLength < defaultPageSize) {
            setHasMore(false);
        }
        setNotifications(function (currentNotifications) { return __spreadArray(__spreadArray([], currentNotifications, true), notificationsList, true); });
    };
    useEffect(function () {
        if (isOpen) {
            // сбрасываем до начальных значений при каждом монтировании
            setHasMore(true);
            var queryData = __assign(__assign({}, defaultFilters), { filter: {}, startIndex: 0 });
            loadNotifications(queryData)
                .then(handleLoadedNotifications)
                .catch(function () {
                setHasMore(false);
            });
            loadCountNotViewedMessages().then(setNotViewedNotifications);
        }
    }, [
        setNotifications,
        loadNotifications,
        isOpen,
        setHasMore,
        loadCountNotViewedMessages,
        setNotViewedNotifications,
    ]);
    var loadMoreNotifications = useCallback(function () {
        setIsNotificationsLoading(true);
        setTimeout(function () {
            var queryData = __assign(__assign({}, defaultFilters), { filter: {}, startIndex: notifications.length });
            loadNotifications(queryData)
                .then(handleLoadedNotifications)
                .finally(function () {
                setNeedLoading(false);
                setIsNotificationsLoading(false);
            });
        }, 500);
    }, [loadNotifications, setHasMore, notifications]);
    useEffect(function () {
        needLoading && loadMoreNotifications();
    }, [needLoading, loadMoreNotifications]);
    var deleteNotification = useCallback(function (id, viewed, catchFunc) {
        var filteredNotifications = notifications.filter(function (notification) { return notification.id !== id; });
        setNotifications(filteredNotifications);
        if (!viewed) {
            setNotViewedNotifications(function (prevValue) { return prevValue - 1; });
        }
        // необходимо для случая, когда удаляем последние уведомления до момента пропадания скролла
        // и нам надо получить уведомления со следующей страницы
        if (filteredNotifications.length <= elementsPerPage && hasMore && !isNotificationsLoading) {
            setNeedLoading(true);
        }
        removeNotification(id).catch(function () {
            setNotifications(notifications);
            setNotViewedNotifications(notViewedNotifications);
            catchFunc();
        });
    }, [
        notifications,
        elementsPerPage,
        hasMore,
        isNotificationsLoading,
        removeNotification,
        setNotifications,
        setNotViewedNotifications,
        notViewedNotifications,
    ]);
    var toggleViewed = useCallback(function (id, viewed) {
        setNotViewedNotifications(function (prevValue) { return (viewed ? prevValue - 1 : prevValue + 1); });
        setNotifications(function (prevNotifications) {
            return prevNotifications.map(function (notification) {
                if (notification.id === id) {
                    return __assign(__assign({}, notification), { viewed: viewed });
                }
                return notification;
            });
        });
        changeViewed(id, viewed).catch(function () {
            setNotViewedNotifications(notViewedNotifications);
            setNotifications(notifications);
        });
    }, [notifications, setNotifications, setNotViewedNotifications, changeViewed, notViewedNotifications]);
    var contextValue = {
        deleteNotification: deleteNotification,
        toggleViewed: toggleViewed,
    };
    var anchorOrigin = {
        vertical: 'bottom',
        horizontal: 'right',
    };
    var transformOrigin = {
        vertical: 'top',
        horizontal: 'right',
    };
    var paperProps = {
        sx: sx.paperContainer,
    };
    var endMessage = React.createElement(EndMessage, { hasNotifications: !!notifications.length });
    var classNames = {
        exit: classes.listTransitionExit,
        exitActive: classes.listTransitionExitActive,
    };
    var notificationElements = notifications.map(function (notification) { return (React.createElement(CSSTransition, { key: notification.id, timeout: 250, classNames: classNames },
        React.createElement(NotificationCard, { notification: notification }))); });
    return (React.createElement(NotificationsContext.Provider, { value: contextValue },
        React.createElement(Popover, { open: isOpen, onClose: setPopoverClose, anchorEl: anchorEl, anchorOrigin: anchorOrigin, transformOrigin: transformOrigin, PaperProps: paperProps },
            React.createElement(NotificationPanelHeader, null),
            React.createElement(Box, { id: scrollablePaperId, sx: sx.messagesContainer },
                React.createElement(InfiniteScroll, { dataLength: notifications.length, next: loadMoreNotifications, scrollThreshold: 0.99, hasMore: hasMore, loader: loader, scrollableTarget: scrollablePaperId, endMessage: endMessage },
                    React.createElement(TransitionGroup, null, notificationElements))))));
};
